import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  FileText,
  Phone,
  Mail,
  MapPin,
  Instagram,
  ExternalLink,
  Calendar,
  Briefcase,
  Star,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  Eye,
  Loader2,
  History
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const APPLICATION_STATUSES = {
  pending: { label: "Pending Review", color: "bg-yellow-500", icon: Clock },
  approved: { label: "Approved", color: "bg-green-500", icon: CheckCircle },
  denied: { label: "Denied", color: "bg-red-500", icon: XCircle },
  pending_info: { label: "More Info Needed", color: "bg-blue-500", icon: AlertCircle }
};

const RenterApplicationsManager = ({ salonId }) => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewConditions, setReviewConditions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  // Stats
  const stats = {
    pending: applications.filter(a => a.status === "pending").length,
    approved: applications.filter(a => a.status === "approved").length,
    denied: applications.filter(a => a.status === "denied").length,
    total: applications.length
  };

  useEffect(() => {
    fetchApplications();
  }, [salonId]);

  const fetchApplications = async () => {
    if (!salonId) return;
    try {
      const response = await axios.get(`${API}/booths/salon/${salonId}/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const reviewApplication = async (status) => {
    if (!selectedApplication) return;
    setSubmitting(true);
    
    try {
      await axios.put(
        `${API}/booths/application/${selectedApplication.application_id}/review`,
        {
          status,
          notes: reviewNotes,
          conditions: status === "approved" ? reviewConditions : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Application ${status}!`);
      setShowReviewDialog(false);
      setSelectedApplication(null);
      setReviewNotes("");
      setReviewConditions("");
      fetchApplications();
    } catch (error) {
      toast.error("Failed to update application");
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewDialog = (application) => {
    setSelectedApplication(application);
    setReviewNotes("");
    setReviewConditions("");
    setShowReviewDialog(true);
  };

  const filteredApplications = applications.filter(app => {
    // Status filter
    if (activeTab !== "all" && app.status !== activeTab) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        app.full_name?.toLowerCase().includes(query) ||
        app.email?.toLowerCase().includes(query) ||
        app.phone?.includes(query) ||
        app.professional_type?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
          <p className="text-stone-400 mt-2">Loading applications...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Total Applications</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Approved</p>
                <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Denied</p>
                <p className="text-2xl font-bold text-red-400">{stats.denied}</p>
              </div>
              <UserX className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Booth Rental Applications
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search applicants..."
                  className="pl-9 bg-stone-700 border-stone-600 text-white w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-stone-700 mb-6">
              <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                Pending ({stats.pending})
              </TabsTrigger>
              <TabsTrigger value="approved" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                Approved ({stats.approved})
              </TabsTrigger>
              <TabsTrigger value="denied" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                Denied ({stats.denied})
              </TabsTrigger>
              <TabsTrigger value="all" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                All ({stats.total})
              </TabsTrigger>
            </TabsList>

            <div className="space-y-4">
              {filteredApplications.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-stone-600 mb-4" />
                  <p className="text-stone-400">No applications found</p>
                </div>
              ) : (
                filteredApplications.map((app) => {
                  const status = APPLICATION_STATUSES[app.status] || APPLICATION_STATUSES.pending;
                  const StatusIcon = status.icon;
                  const isExpanded = expandedId === app.application_id;

                  return (
                    <div
                      key={app.application_id}
                      className="border border-stone-700 rounded-lg overflow-hidden"
                    >
                      {/* Header Row */}
                      <div
                        className="p-4 bg-stone-700/30 flex items-center justify-between cursor-pointer hover:bg-stone-700/50 transition-colors"
                        onClick={() => setExpandedId(isExpanded ? null : app.application_id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-stone-700 flex items-center justify-center">
                            <span className="text-xl font-bold text-amber-400">
                              {app.full_name?.[0]?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-semibold">{app.full_name}</h3>
                              <Badge className={`${status.color} text-white text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                            </div>
                            <p className="text-stone-400 text-sm">
                              {app.professional_type?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())} • 
                              {app.years_experience} years experience
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-stone-400 text-xs">Applied</p>
                            <p className="text-stone-300 text-sm">{formatDate(app.submitted_at)}</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-stone-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-stone-500" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 border-t border-stone-700 bg-stone-800/30">
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Contact Info */}
                            <div className="space-y-3">
                              <h4 className="text-white font-medium flex items-center gap-2">
                                <Users className="w-4 h-4 text-amber-500" />
                                Contact Information
                              </h4>
                              <div className="space-y-2 text-sm">
                                <p className="flex items-center gap-2 text-stone-300">
                                  <Mail className="w-4 h-4 text-stone-500" />
                                  {app.email}
                                </p>
                                <p className="flex items-center gap-2 text-stone-300">
                                  <Phone className="w-4 h-4 text-stone-500" />
                                  {app.phone}
                                </p>
                                {app.instagram_handle && (
                                  <p className="flex items-center gap-2 text-stone-300">
                                    <Instagram className="w-4 h-4 text-stone-500" />
                                    @{app.instagram_handle}
                                  </p>
                                )}
                                {app.portfolio_url && (
                                  <a 
                                    href={app.portfolio_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-amber-400 hover:underline"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    View Portfolio
                                  </a>
                                )}
                              </div>
                            </div>

                            {/* Professional Info */}
                            <div className="space-y-3">
                              <h4 className="text-white font-medium flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-amber-500" />
                                Professional Details
                              </h4>
                              <div className="space-y-2 text-sm">
                                {app.license_number && (
                                  <p className="text-stone-300">
                                    <span className="text-stone-500">License:</span> {app.license_number}
                                  </p>
                                )}
                                <p className="text-stone-300">
                                  <span className="text-stone-500">Experience:</span> {app.years_experience} years
                                </p>
                                {app.preferred_days?.length > 0 && (
                                  <p className="text-stone-300">
                                    <span className="text-stone-500">Preferred Days:</span> {app.preferred_days.join(", ")}
                                  </p>
                                )}
                                {app.preferred_hours && (
                                  <p className="text-stone-300">
                                    <span className="text-stone-500">Preferred Hours:</span> {app.preferred_hours}
                                  </p>
                                )}
                                {app.move_in_date && (
                                  <p className="text-stone-300">
                                    <span className="text-stone-500">Move-in Date:</span> {app.move_in_date}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* About Me */}
                            {app.about_me && (
                              <div className="space-y-2 md:col-span-2">
                                <h4 className="text-white font-medium">About Me</h4>
                                <p className="text-stone-300 text-sm bg-stone-700/30 p-3 rounded">
                                  {app.about_me}
                                </p>
                              </div>
                            )}

                            {/* Why Interested */}
                            {app.why_interested && (
                              <div className="space-y-2 md:col-span-2">
                                <h4 className="text-white font-medium">Why They&apos;re Interested</h4>
                                <p className="text-stone-300 text-sm bg-stone-700/30 p-3 rounded">
                                  {app.why_interested}
                                </p>
                              </div>
                            )}

                            {/* References */}
                            {app.references?.length > 0 && (
                              <div className="space-y-2 md:col-span-2">
                                <h4 className="text-white font-medium flex items-center gap-2">
                                  <Star className="w-4 h-4 text-amber-500" />
                                  References ({app.references.length})
                                </h4>
                                <div className="grid md:grid-cols-2 gap-3">
                                  {app.references.map((ref, idx) => (
                                    <div key={idx} className="bg-stone-700/30 p-3 rounded text-sm">
                                      <p className="text-white font-medium">{ref.name}</p>
                                      <p className="text-stone-400">{ref.relationship}</p>
                                      <p className="text-stone-300">{ref.phone}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Review History */}
                            {app.review_notes?.length > 0 && (
                              <div className="space-y-2 md:col-span-2">
                                <h4 className="text-white font-medium flex items-center gap-2">
                                  <History className="w-4 h-4 text-amber-500" />
                                  Review History
                                </h4>
                                <div className="space-y-2">
                                  {app.review_notes.map((note, idx) => (
                                    <div key={idx} className="bg-stone-700/30 p-3 rounded text-sm border-l-2 border-amber-500">
                                      <div className="flex justify-between items-start mb-1">
                                        <Badge className={APPLICATION_STATUSES[note.status]?.color || "bg-stone-600"}>
                                          {APPLICATION_STATUSES[note.status]?.label || note.status}
                                        </Badge>
                                        <span className="text-stone-500 text-xs">{formatDate(note.reviewed_at)}</span>
                                      </div>
                                      {note.notes && <p className="text-stone-300">{note.notes}</p>}
                                      {note.conditions && (
                                        <p className="text-amber-400 mt-1">
                                          <span className="text-stone-500">Conditions:</span> {note.conditions}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          {app.status === "pending" && (
                            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stone-700">
                              <Button 
                                variant="outline" 
                                className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                                onClick={() => openReviewDialog(app)}
                              >
                                <AlertCircle className="w-4 h-4 mr-2" />
                                Request More Info
                              </Button>
                              <Button 
                                variant="outline" 
                                className="border-red-500 text-red-400 hover:bg-red-500/10"
                                onClick={() => {
                                  setSelectedApplication(app);
                                  reviewApplication("denied");
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Deny
                              </Button>
                              <Button 
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => openReviewDialog(app)}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="bg-stone-800 border-stone-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Review Application - {selectedApplication?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-stone-300">Notes (Optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this decision..."
                className="bg-stone-700 border-stone-600 text-white"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-stone-300">Approval Conditions (Optional)</Label>
              <Textarea
                value={reviewConditions}
                onChange={(e) => setReviewConditions(e.target.value)}
                placeholder="e.g., Must provide proof of insurance before move-in"
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                className="border-stone-600"
                onClick={() => setShowReviewDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="outline"
                className="border-blue-500 text-blue-400"
                onClick={() => reviewApplication("pending_info")}
                disabled={submitting}
              >
                Request Info
              </Button>
              <Button 
                variant="destructive"
                onClick={() => reviewApplication("denied")}
                disabled={submitting}
              >
                Deny
              </Button>
              <Button 
                className="bg-green-500 hover:bg-green-600"
                onClick={() => reviewApplication("approved")}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RenterApplicationsManager;
